import { useAudit } from '../api/hooks';
import type { AuditEntry } from '../api/types';
import { useState } from 'react';
import { label, show } from './audit-format';
import { Alert, Button, Field, Note, PageHeader, Panel, Spinner, Table } from '../ui';

/**
 * A-25 — the immutable log.
 *
 * This rendered `before` and `after` as two columns of raw JSON, which is the
 * shape the row is stored in rather than the question a reader has. Nobody wants
 * to diff `{"name":"Arabic","endonym":"العربية","isActive":false}` against a
 * near-identical blob by eye — and with both on screen the table was wider than
 * the viewport, so the `after` column was clipped off the edge entirely.
 *
 * It now renders **what changed**: the fields that actually differ, old beside
 * new. Nothing is lost — the row still holds both sides in full. It was the
 * presentation that was wrong, not the record.
 */

type Bag = Record<string, unknown>;

const asBag = (v: unknown): Bag =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Bag) : {};

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n)}…` : s);

/**
 * Only the fields that moved.
 *
 * A creation has no `before`, so its fields read as what was set rather than as
 * a change from nothing — which is why the arrow is suppressed there.
 */
function Change({ entry }: { entry: AuditEntry }) {
  const before = asBag(entry.before);
  const after = asBag(entry.after);
  const isCreation = entry.before === null || entry.before === undefined;

  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]));

  if (keys.length === 0) return <span className="mk-dim">—</span>;

  return (
    <div className="mk-diff">
      {keys.map((k) => (
        <div key={k} className="mk-diff-row">
          <span className="mk-diff-k">{label(k)}</span>
          {isCreation ? (
            <span className="mk-diff-to">{clip(show(after[k]), 70)}</span>
          ) : (
            <>
              <span className="mk-diff-from">{clip(show(before[k]), 44)}</span>
              <span className="mk-diff-arrow" aria-hidden>→</span>
              <span className="mk-diff-to">{clip(show(after[k]), 44)}</span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

/** D49 — stored UTC, rendered in the viewer's zone. Date over time, never wrapped. */
function When({ at }: { at: string }) {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return <span className="mk-dim">{at}</span>;
  return (
    <span className="mk-when">
      <b>{d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}</b>
      {d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
    </span>
  );
}

export function Audit() {
  /*
   * AU-04 — the search is SUBMITTED, not typed-into-the-query.
   *
   * It filters on the server, so every keystroke would be a request over a table that only
   * grows, and the one before last would still be in flight when the next arrived. A person
   * searching an audit log is looking for something specific — a retailer, an action, a
   * reference from a complaint — and types it before they want an answer.
   */
  const [draft, setDraft] = useState('');
  const [applied, setApplied] = useState<{ q?: string; from?: string; to?: string }>({});
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading, error } = useAudit({ limit: 100, ...applied });
  const submit = () => setApplied({
    ...(draft.trim() ? { q: draft.trim() } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  });
  const filtered = applied.q !== undefined || applied.from !== undefined || applied.to !== undefined;

  if (error) return <Alert tone="stop">{error.message}</Alert>;

  return (
    <>
      <PageHeader title="Audit log" />
      <Panel pad>
        <form
          className="mk-audit-filter"
          onSubmit={(e) => { e.preventDefault(); submit(); }}
        >
          <Field label="Search" hint="Who did it, what the action was, or what it was done to.">
            <input
              className="mk-fld"
              type="search"
              value={draft}
              placeholder="A retailer, an email, an action, a reference…"
              onChange={(e) => setDraft(e.target.value)}
            />
          </Field>
          <Field label="From"><input className="mk-fld" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="To"><input className="mk-fld" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
          <div className="mk-audit-filter-go">
            <Button type="submit">Search</Button>
            {filtered ? (
              <Button
                tone="ghost"
                onClick={() => { setDraft(''); setFrom(''); setTo(''); setApplied({}); }}
              >
                Clear
              </Button>
            ) : null}
          </div>
        </form>
      </Panel>
      <Alert tone="them">
        <b>Nothing here can be edited or deleted.</b> UPDATE and DELETE are revoked from the
        application's database role, so the log cannot be rewritten even by code that tries.
        Retained seven years (AU-05).
      </Alert>
      <Panel>
        {/*
         * The spinner is INSIDE the panel, below the filter form, rather than replacing the
         * whole screen. A search that makes the search box disappear while it runs takes away
         * the thing you were about to correct when the result is wrong.
         */}
        {isLoading ? <Spinner /> : (
        <Table<AuditEntry>
          rows={data ?? []}
          rowKey={(a) => `${a.at}-${a.action}-${a.object.id}`}
          columns={[
            { header: 'When', width: '1%', render: (a) => <When at={a.at} /> },
            { header: 'Who', width: '1%', render: (a) => <span className="mk-nowrap">{a.actor}</span> },
            {
              header: 'Action', width: '1%',
              // The action and the thing it happened to are one fact, and
              // splitting them across two columns cost width the diff needed.
              render: (a) => (
                <>
                  <code className="mk-nowrap">{a.action}</code>
                  <div className="mk-hint">{a.object.label ?? a.object.type}</div>
                </>
              ),
            },
            { header: 'What changed', render: (a) => <Change entry={a} /> },
            {
              // Deliberately unconstrained, like the diff: a reason is a sentence
              // somebody typed, and squeezing it to its content wrapped it four
              // words to a line.
              header: 'Reason',
              render: (a) => (a.reason
                ? <span className="mk-dim">{a.reason}</span>
                : <span className="mk-dim">—</span>),
            },
          ]}
        />
        )}
      </Panel>
      <Note>
        The value before a change is stored on the row. Six months from now nobody has to
        reconstruct it from a sequence of events.
      </Note>
    </>
  );
}
